/**
 * Which face of the guide to open with.
 *
 * The guide has two renderers over one body of content — a slide-by-slide
 * walkthrough for a first read, and the scrollable page for looking things up —
 * and the only interesting question is which one someone lands on. That decision
 * lives here, out of the component, because it has four inputs and every one of
 * them was a way to get it wrong.
 */

export type GuideMode = 'guided' | 'reference';
/** 'start' isn't a mode, it's the question — hence its own type. */
export type GuideEntry = GuideMode | 'start';

export const MODE_KEY = 'pmt.training.mode';

const isMode = (s: unknown): s is GuideMode => s === 'guided' || s === 'reference';

/**
 * @param forceStart     `?start=1`, or "Change role" from inside the walkthrough
 * @param deepLinkTopic  `?topic=` — a link to ONE topic. Nothing in the app
 *                       produces it today; it is for links people share by
 *                       hand. The `?` Help drawer links to /training with no
 *                       topic, so don't describe this as the Help-drawer path
 *                       until it actually is one.
 * @param stored         the last mode they chose, from localStorage
 * @param hasProgress    they have ticked at least one topic before
 */
export function entryMode({ forceStart, deepLinkTopic, stored, hasProgress }: {
  forceStart?: boolean;
  deepLinkTopic?: string | null;
  stored?: string | null;
  hasProgress: boolean;
}): GuideEntry {
  // Asking for the picker outranks every guess below it. Without this the screen
  // was unreachable for anyone who had ticked a single topic — which is everyone
  // who used the guide before this shipped.
  if (forceStart) return 'start';

  // ⚠️ A DEEP LINK ALWAYS WINS. The Help drawer links to one topic; dropping
  // that person into slide 1 of a walkthrough answers a question they didn't
  // ask and loses the one they did. Not remembered either — it's this visit.
  if (deepLinkTopic) return 'reference';

  // Chosen once, then honoured. Being asked to pick a role on every visit is
  // the onboarding equivalent of a cookie banner.
  if (isMode(stored)) return stored;

  // Someone already part-way through the guide predates guided mode (Phase 3
  // shipped ticks first). They've been reading the page; don't reintroduce them
  // to the app with a role picker.
  return hasProgress ? 'reference' : 'start';
}

/**
 * What the URL should become for each thing the reader can do.
 *
 * ⚠️ THE POINT IS THAT EACH ACTION IS ONE TRANSITION. Composing these as two
 * successive updates inside a handler is what broke role selection: both were
 * built from the parameters of the render still on screen, so the second
 * silently reverted the first and the picked role was discarded (review, #289).
 * A pure function returning the FINAL parameters makes that shape impossible —
 * and, unlike the handler it replaces, can be tested.
 *
 *   track — the picker pills on the reference page: change track, stay put
 *   role  — a card on the "Start here" screen: change track AND start walking
 *   mode  — a deliberate switch of mode; drops overrides the reader has left
 *   start — back to the role picker, in the URL so a refresh keeps it
 */
export type GuideAction =
  | { kind: 'track'; id: string }
  | { kind: 'role'; id: string }
  | { kind: 'mode' }
  | { kind: 'start' };

/** `start` and `guided` steer which mode opens; any deliberate mode change has to
 *  clear them, or a refresh re-applies an override that's been moved on from. */
const MODE_PARAMS = ['start', 'guided'] as const;

export function nextGuideParams(current: URLSearchParams, a: GuideAction): URLSearchParams {
  const p = new URLSearchParams(current);
  switch (a.kind) {
    case 'track':
      p.set('track', a.id);
      p.delete('topic');            // a highlighted card in the old track means nothing here
      return p;
    case 'role':
      p.set('track', a.id);
      p.delete('topic');
      for (const k of MODE_PARAMS) p.delete(k);
      return p;
    case 'mode':
      for (const k of MODE_PARAMS) p.delete(k);
      return p;
    case 'start':
      p.set('start', '1');
      p.delete('topic');
      // `start` is set, not cleared, so only `guided` goes.
      p.delete('guided');
      return p;
  }
}

/** localStorage, but never fatal — a blocked store just means "not chosen yet". */
export function readMode(): string | null {
  try { return window.localStorage.getItem(MODE_KEY); } catch { return null; }
}

export function rememberMode(m: GuideMode): void {
  try { window.localStorage.setItem(MODE_KEY, m); } catch { /* private mode / quota — the mode still applies this visit */ }
}
